import React, { useState, useMemo } from 'react';
import clsx from 'clsx';
import { useUser } from './UserContext';
import { Button } from './button';
import { Input } from './input';
import { Checkbox } from './checkbox';

// Color palette based on UID hash (same as AssignedUsersBubbles)
const AVATAR_COLORS = [
  'bg-azul-surelia/20 text-azul-surelia',
  'bg-verde-lima/30 text-azul-surelia',
  'bg-cian-digital/20 text-cian-digital',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
  'bg-indigo-100 text-indigo-700',
];

const getAvatarColor = (uid) => {
  if (!uid) return AVATAR_COLORS[0];
  const hash = uid.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const UserAssignmentSelector = ({
  assignedUsers = [],
  lockedUsers = [],
  onSave,
  onCancel,
  title = 'Asignar Usuarios',
  saveLabel = 'Guardar',
  cancelLabel = 'Cancelar',
  lockedLabel = 'Asignado al cliente',
}) => {
  const { orgUsersList, getInitials, user } = useUser();
  const lockedSet = useMemo(() => new Set(lockedUsers), [lockedUsers]);
  const [selectedUsers, setSelectedUsers] = useState(new Set(assignedUsers));
  const [searchTerm, setSearchTerm] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return orgUsersList;
    const term = searchTerm.toLowerCase();
    return orgUsersList.filter(
      (u) =>
        u.nombre?.toLowerCase().includes(term) ||
        u.correo?.toLowerCase().includes(term)
    );
  }, [orgUsersList, searchTerm]);

  const handleToggleUser = (uid) => {
    // Don't allow toggling locked users
    if (lockedSet.has(uid)) return;

    setSelectedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(Array.from(selectedUsers));
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = useMemo(() => {
    if (selectedUsers.size !== assignedUsers.length) return true;
    return !assignedUsers.every((uid) => selectedUsers.has(uid));
  }, [selectedUsers, assignedUsers]);

  return (
    <div className="flex flex-col">
      {/* Search input */}
      <div className="mb-4">
        <Input
          type="text"
          placeholder="Buscar por nombre o correo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full"
        />
      </div>

      {/* Users list */}
      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
        {filteredUsers.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            {searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios en la organización'}
          </div>
        ) : (
          filteredUsers.map((orgUser) => {
            const isLocked = lockedSet.has(orgUser.id);
            const isSelected = isLocked || selectedUsers.has(orgUser.id);
            const isCurrentUser = orgUser.id === user?.uid;

            return (
              <div
                key={orgUser.id}
                className={clsx(
                  'flex items-center gap-3 p-3 transition-colors',
                  isLocked ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer',
                  !isLocked && isSelected ? 'bg-azul-surelia/5' : '',
                  !isLocked && !isSelected ? 'hover:bg-gray-50' : ''
                )}
                onClick={() => handleToggleUser(orgUser.id)}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => handleToggleUser(orgUser.id)}
                  color={isLocked ? 'zinc' : 'indigo'}
                  disabled={isLocked}
                />

                {/* Avatar */}
                <div
                  className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium',
                    isLocked ? 'opacity-60' : '',
                    getAvatarColor(orgUser.id)
                  )}
                >
                  {getInitials(orgUser.id)}
                </div>

                {/* User info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={clsx(
                      'font-medium truncate',
                      isLocked ? 'text-gray-500' : 'text-gray-900'
                    )}>
                      {orgUser.nombre || 'Sin nombre'}
                    </span>
                    {isCurrentUser && (
                      <span className="text-xs bg-azul-surelia/10 text-azul-surelia px-2 py-0.5 rounded-full">
                        Tú
                      </span>
                    )}
                    {isLocked && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                        {lockedLabel}
                      </span>
                    )}
                  </div>
                  <span className={clsx(
                    'text-sm truncate block',
                    isLocked ? 'text-gray-400' : 'text-gray-500'
                  )}>
                    {orgUser.correo}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Selected count */}
      <div className="mt-3 text-sm text-gray-600">
        {(() => {
          const totalSelected = selectedUsers.size + lockedUsers.length;
          if (totalSelected === 0) {
            return <span>Ningún usuario seleccionado</span>;
          } else if (totalSelected === 1) {
            return <span>1 usuario seleccionado</span>;
          } else {
            const parts = [];
            if (lockedUsers.length > 0) {
              parts.push(`${lockedUsers.length} del cliente`);
            }
            if (selectedUsers.size > 0) {
              parts.push(`${selectedUsers.size} del contrato`);
            }
            return <span>{totalSelected} usuarios ({parts.join(', ')})</span>;
          }
        })()}
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
        <Button outline color="zinc" onClick={onCancel} disabled={isSaving}>
          {cancelLabel}
        </Button>
        <Button
          color="surelia"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        >
          {isSaving ? 'Guardando...' : saveLabel}
        </Button>
      </div>
    </div>
  );
};

export default UserAssignmentSelector;
